<template>
  <div class="translation-form">
    <div class="form-header">
      <h2 class="form-title">{{ getFormTitle() }}</h2>
      <p class="form-subtitle">{{ getFormSubtitle() }}</p>
    </div>

    <div class="upload-section">
      <ClientOnly>
        <template v-if="shouldShowTaskList">
          <div class="progress-replacer">
            <InlineTaskStatusList
              :hide-title="true"
              :category="props.category"
              @run-in-background="handleRunInBackground"
            />
          </div>
        </template>
        <template v-else>
          <FileUpload
            ref="fileUploadRef"
            :accept="fileAccept"
            :allowed-extensions="allowedExtensions"
            :support-text="supportText"
            :supported-formats="supportedChips"
            :page-count="uploadedPages"
            @file-selected="handleFileSelected"
            @file-removed="handleFileRemoved"
            @file-type-mismatch="onFileTypeMismatch"
          />
        </template>
      </ClientOnly>

      <div class="page-count-explanation">
        <el-popover
          placement="bottom"
          :width="320"
          trigger="hover"
          popper-class="page-count-popover"
        >
          <template #reference>
            <span class="rule-trigger">
              <Icon name="material-symbols:info-outline" size="15" />
              {{ $t('categoryForm.pageCountExplanation.trigger') }}
            </span>
          </template>

          <div class="rule-content">
            <div class="rule-group">
              <div class="rule-title">{{ $t('categoryForm.pageCountExplanation.physical.title') }}</div>
              <div class="rule-list">
                <div
                  v-for="(item, i) in $tm('categoryForm.pageCountExplanation.physical.items')"
                  :key="`p${i}`"
                  class="rule-item"
                >
                  <span class="item-label">{{ $rt(item.label) }}</span>
                  <span class="item-ext">{{ $rt(item.ext) }}</span>
                </div>
              </div>
            </div>

            <div class="rule-group">
              <div class="rule-title">{{ $t('categoryForm.pageCountExplanation.chars.title') }}</div>
              <div class="rule-list">
                <div
                  v-for="(item, i) in $tm('categoryForm.pageCountExplanation.chars.items')"
                  :key="`c${i}`"
                  class="rule-item"
                >
                  <span class="item-label">{{ $rt(item.label) }}</span>
                  <span class="item-ext">{{ $rt(item.ext) }}</span>
                </div>
              </div>
            </div>
          </div>
        </el-popover>
      </div>

      <p v-if="shouldShowTaskList" class="upload-hint">
        {{ $t('categoryForm.hint.prefix') }}
        <NuxtLink :to="localePath('/my-page/records')" class="hint-link">{{ $t('categoryForm.hint.linkText') }}</NuxtLink>
        {{ $t('categoryForm.hint.suffix') }}
      </p>
    </div>

    <div class="translation-settings">
      <ClientOnly>
        <div class="language-selection">
          <div class="language-group">
            <label class="language-label">
              <Icon name="material-symbols:language" size="18" />
              {{ $t('form.labels.src') }}
            </label>
            <el-select
              v-model="sourceLanguage"
              :placeholder="srcPlaceholder"
              size="large"
              class="language-select"
              :loading="!translationStore.initialized"
            >
              <el-option
                v-for="lang in srcLanguageList"
                :key="lang.code"
                :label="lang.name"
                :value="lang.code"
              />
            </el-select>
          </div>

          <div class="arrow-icon">
            <Icon name="material-symbols:arrow-forward" size="24" />
          </div>

          <div class="language-group">
            <label class="language-label">
              <Icon name="material-symbols:translate" size="18" />
              {{ $t('form.labels.dst') }}
            </label>
            <el-select
              v-model="targetLanguage"
              :placeholder="dstPlaceholder"
              size="large"
              class="language-select"
              :loading="!translationStore.initialized"
            >
              <el-option
                v-for="lang in dstLanguageList"
                :key="lang.code"
                :label="lang.name"
                :value="lang.code"
              />
            </el-select>
          </div>
        </div>
        <template #fallback>
          <div class="language-selection">
            <div class="language-group">
              <label class="language-label">{{ $t('form.labels.src') }}</label>
              <el-input disabled :placeholder="$t('form.placeholders.loading')" />
            </div>
            <div class="arrow-icon">
              <Icon name="material-symbols:arrow-forward" size="24" />
            </div>
            <div class="language-group">
              <label class="language-label">{{ $t('form.labels.dst') }}</label>
              <el-input disabled :placeholder="$t('form.placeholders.loading')" />
            </div>
          </div>
        </template>
      </ClientOnly>
    </div>

    <ClientOnly>
      <div class="form-actions">
        <template v-if="shouldShowTaskList && isTranslating">
          <button class="translate-button" disabled>
            <Icon name="mdi:loading" size="20" class="rotating" />
            {{ $t('taskItem.status.translating') }}
          </button>
        </template>

        <template v-else-if="uploadStatus === UPLOAD_STATE.UPLOADED">
          <button
            class="translate-button"
            :disabled="isSubmitting"
            @click="handleStartTranslation"
          >
            <Icon name="material-symbols:translate" size="20" />
            {{ isSubmitting ? $t('form.buttons.submitting') : $t('form.buttons.submit') }}
          </button>
        </template>

        <template v-else-if="uploadStatus === UPLOAD_STATE.SCANNED">
          <div class="scanned-warning">
            <div class="scanned-warning-icon">
              <Icon name="material-symbols:warning" size="28" />
            </div>
            <div class="scanned-warning-content">
              <h3 class="scanned-warning-title">{{ $t('categoryForm.scannedPdf.title', '该 PDF 为扫描版 / 图片型 PDF') }}</h3>
              <p class="scanned-warning-desc">{{ $t('categoryForm.scannedPdf.description', '当前暂不支持扫描版 PDF 翻译。建议先用第三方 OCR 工具（如 Adobe Acrobat、ABBYY）将扫描件转为文字型 PDF 后再上传。') }}</p>
              <button class="reselect-button" @click="handleReselectFile">
                <Icon name="material-symbols:refresh" size="18" />
                {{ $t('categoryForm.scannedPdf.reselect', '重新选择文件') }}
              </button>
            </div>
          </div>
        </template>

        <template v-else-if="uploadStatus === UPLOAD_STATE.UPLOADING">
          <button class="translate-button" disabled>
            <Icon name="mdi:loading" size="20" class="rotating" />
            {{ $t('form.buttons.uploading', '上传分析中...') }}
          </button>
        </template>

        <template v-else>
          <button
            class="translate-button"
            :disabled="!canUpload"
            @click="handleRetryUpload"
          >
            <Icon :name="uploadStatus === UPLOAD_STATE.FAILED ? 'material-symbols:refresh' : 'material-symbols:cloud-upload'" size="20" />
            {{ uploadStatus === UPLOAD_STATE.FAILED ? $t('form.buttons.retryUpload', '重试上传') : $t('form.buttons.uploadAnalyze', '上传分析文档') }}
          </button>
        </template>
      </div>
      <template #fallback>
        <div class="form-actions">
          <button class="translate-button" disabled>
            <Icon name="material-symbols:translate" size="20" />
            {{ $t('form.placeholders.loading') }}
          </button>
        </div>
      </template>
    </ClientOnly>

    <UploadConfirmTranslateDialog
      :visible="flowState.confirmDialog.visible"
      :file-name="flowState.confirmDialog.fileName"
      :pages="flowState.confirmDialog.pages"
      :source-language="flowState.confirmDialog.sourceLanguage"
      :target-language="flowState.confirmDialog.targetLanguage"
      :source-language-options="srcLanguageList"
      :target-language-options="dstLanguageList"
      @cancel="handleCancelConfirmDialog"
      @confirm="handleConfirmStartTranslation"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { ElMessage } from 'element-plus'
import { useRouter } from 'vue-router'
import FileUpload from './FileUpload.vue'
import UploadConfirmTranslateDialog from './UploadConfirmTranslateDialog.vue'
import { useTranslationStore } from '~/stores/translation'
import type { Int64Id, LanguageInfo } from '~/types/translation'
import { useUserStore } from '~/stores/user'
import { useTranslationApi } from '~/composables/useTranslationApi'
import InlineTaskStatusList from '~/components/shared/InlineTaskStatusList.vue'
import { useFileTypeDetection } from '~/composables/useFileTypeDetection'
import { useSmartRedirect } from '~/composables/useSmartRedirect'
import { useFileRedirectStore } from '~/stores/fileRedirect'
import { useLocalePath } from '#imports'
import { useUploadApi } from '~/composables/useUploadApi'
import { createAutoUploadConfirmFlow, UPLOAD_STATUS } from '~/utils/autoUploadConfirmFlow.js'
import { applyTranslationStartFailureFromResult } from '~/utils/translationStartFailureOrchestrator.js'

interface Props {
  category?: string
}

const props = withDefaults(defineProps<Props>(), {
  category: 'general'
})

const emit = defineEmits<{
  startTranslation: [settings: any]
  clearForm: []
}>()

const translationStore = useTranslationStore()
const userStore = useUserStore()
const router = useRouter()
const { t } = useI18n()
const localePath = useLocalePath()
const fileRedirect = useFileRedirectStore()
const { detectCategoryByExt, allowedExtensionsForCategory, acceptForCategory } = useFileTypeDetection()
const { smartRedirect } = useSmartRedirect()

const sourceLanguage = ref('')
const targetLanguage = ref('')
const fileUploadRef = ref<InstanceType<typeof FileUpload> | null>(null)
const showTaskList = ref(true)

const flow = createAutoUploadConfirmFlow({
  uploadFile: async (file: File) => {
    const { uploadFile } = useUploadApi()
    return uploadFile(file)
  },
  getLimitInfo: async () => {
    const { getLimitInfo } = useTranslationApi()
    return getLimitInfo()
  },
  createTaskWithFileId: async (
    fileId: Int64Id,
    fileName: string,
    to: string,
    from: string,
    category: string
  ) => {
    return translationStore.createTaskWithFileId(fileId, fileName, to, from, category)
  },
  createState: (initial) => reactive(initial)
})

const flowState = flow.state
const UPLOAD_STATE = UPLOAD_STATUS

const srcLanguageList = computed<LanguageInfo[]>(() => translationStore.srcLanguageList)
const dstLanguageList = computed<LanguageInfo[]>(() => translationStore.dstLanguageList)
const canUpload = computed(() => {
  return !!flowState.selectedFile && !!sourceLanguage.value && !!targetLanguage.value
})
const srcPlaceholder = computed(() => (translationStore.initialized ? t('form.placeholders.src') : t('form.placeholders.loading')))
const dstPlaceholder = computed(() => (translationStore.initialized ? t('form.placeholders.dst') : t('form.placeholders.loading')))
const uploadStatus = computed(() => flowState.uploadStatus)
const uploadedPages = computed(() => flowState.uploadedPages)
const isSubmitting = computed(() => flowState.isSubmitting)

const hasDisplayTasks = computed(() => {
  const category = props.category || 'general'
  return translationStore.tasks.some(t => t.category === category)
})
const shouldShowTaskList = computed(() => hasDisplayTasks.value && showTaskList.value)
const isTranslating = computed(() => {
  const category = props.category || 'general'
  return translationStore.activeTasks.some(t => t.category === category)
})

onMounted(async () => {
  try {
    await translationStore.initialize()
    sourceLanguage.value = translationStore.defaultSrc
    targetLanguage.value = translationStore.defaultDst
  } catch (error) {
    ElMessage.error(t('form.messages.languagesLoadFailed'))
  }

  if (import.meta.client) {
    const pending = fileRedirect.consumePending()
    if (pending?.file && fileUploadRef.value && typeof (fileUploadRef.value as any).setFile === 'function') {
      ;(fileUploadRef.value as any).setFile(pending.file)
      ElMessage.success(t('upload.messages.fileRestored', '已为你加载刚才选择的文件'))
    }
  }
})

const getFormTitle = (): string => {
  const titles = {
    pdf: t('category.pdf.hero.title'),
    office: t('nav.office'),
    ebook: t('nav.ebook'),
    subtitle: t('nav.subtitle'),
    general: t('form.title')
  } as const
  const key = (props.category || 'general') as keyof typeof titles
  return titles[key] ?? titles.general
}

const getFormSubtitle = (): string => {
  const subtitles = {
    pdf: t('category.pdf.hero.subtitle'),
    office: t('categoryForm.subtitle.office'),
    ebook: t('categoryForm.subtitle.ebook'),
    subtitle: t('categoryForm.subtitle.subtitle'),
    general: t('form.subtitle')
  } as const
  const key = (props.category || 'general') as keyof typeof subtitles
  return subtitles[key] ?? subtitles.general
}

const ensureReadyForAutoUpload = () => {
  if (!sourceLanguage.value || !targetLanguage.value) {
    ElMessage.warning(t('categoryForm.messages.chooseBoth'))
    return false
  }

  if (!userStore.isLoggedIn) {
    userStore.setAuthModalVisible(true)
    ElMessage.info(t('form.messages.loginRequired'))
    return false
  }

  return true
}

const executeAutoUpload = async () => {
  const uploadResult = await flow.retryUpload({
    sourceLanguage: sourceLanguage.value,
    targetLanguage: targetLanguage.value
  })

  if (!uploadResult.ok) {
    if (uploadResult.reason === 'upload_failed') {
      const message = uploadResult.error?.message || t('upload.messages.failed', '上传失败，请重试')
      ElMessage.error(message)
    }
    return
  }

  if (uploadResult.reason === 'scanned_pdf') {
    return
  }

  ElMessage.success(t('upload.messages.success', { pages: uploadResult.uploadedPages || '?' }))
}

const handleFileSelected = async (file: File) => {
  flow.setSelectedFile(file)

  if (!ensureReadyForAutoUpload()) {
    return
  }

  await executeAutoUpload()
}

const handleRetryUpload = async () => {
  if (!canUpload.value) {
    ElMessage.warning(t('form.messages.selectFile'))
    return
  }

  if (!ensureReadyForAutoUpload()) {
    return
  }

  await executeAutoUpload()
}

const handleFileRemoved = () => {
  flow.clearSelectedFile()
}

const handleReselectFile = () => {
  handleClearForm()
}

const handleRunInBackground = () => {
  showTaskList.value = false
  handleClearForm()
}

const applyTranslationResult = async (result: Awaited<ReturnType<typeof flow.startTranslation>>) => {
  if (!result.ok) {
    await applyTranslationStartFailureFromResult({
      reason: result.reason,
      localePath,
      router,
      t,
      showWarning: (message: string) => {
        ElMessage.warning(message)
      },
      showError: (message: string) => {
        ElMessage.error(message)
      }
    })
    return
  }

  showTaskList.value = true
  ElMessage.success(t('categoryForm.messages.created'))
  emit('startTranslation', {
    category: props.category,
    sourceLanguage: sourceLanguage.value,
    targetLanguage: targetLanguage.value,
    taskId: result.taskId
  })
}

const handleStartTranslation = async () => {
  const result = await flow.startTranslation({
    sourceLanguage: sourceLanguage.value,
    targetLanguage: targetLanguage.value,
    category: props.category || 'general'
  })

  await applyTranslationResult(result)
}

const handleConfirmStartTranslation = async ({ sourceLanguage: src, targetLanguage: dst }: { sourceLanguage: string; targetLanguage: string }) => {
  sourceLanguage.value = src
  targetLanguage.value = dst

  const result = await flow.confirmAndStartTranslation({
    sourceLanguage: src,
    targetLanguage: dst,
    category: props.category || 'general'
  })

  await applyTranslationResult(result)
}

const handleCancelConfirmDialog = () => {
  flow.cancelConfirm()
}

const handleClearForm = () => {
  sourceLanguage.value = translationStore.defaultSrc
  targetLanguage.value = translationStore.defaultDst
  if (fileUploadRef.value) {
    fileUploadRef.value.clearFile()
  }
  flow.clearSelectedFile()
}

const setTargetLanguage = (langValue: string) => {
  targetLanguage.value = langValue
}

const onFileTypeMismatch = async (file: File) => {
  const to = detectCategoryByExt(file.name)
  if (!to) {
    return
  }
  const from = props.category || 'general'
  await smartRedirect(file, from, to)
}

defineExpose({
  handleClearForm,
  setTargetLanguage,
  fileUploadRef
})

const fileAccept = computed(() => acceptForCategory(props.category))
const allowedExtensions = computed(() => allowedExtensionsForCategory(props.category))

const supportText = computed(() => {
  const cat = (props.category || 'general').toLowerCase()
  if (cat === 'pdf') return t('categoryForm.supportText.pdf')
  if (cat === 'office') return t('categoryForm.supportText.office')
  if (cat === 'ebook') return t('categoryForm.supportText.ebook')
  if (cat === 'subtitle') return t('categoryForm.supportText.subtitle')
  return t('categoryForm.supportText.default')
})

const supportedChips = computed(() => {
  const exts = allowedExtensions.value
  if (exts && exts.length > 0) {
    return exts.map(x => (x.startsWith('.') ? x : `.${x}`))
  }
  return ['.docx', '.xlsx', '.pptx', '.pdf', '.epub', '.srt']
})
</script>
<style scoped lang="scss">
@use 'sass:color';

.translation-form {
  width: 100%;
  background: rgba($white, 0.88);
  border-radius: $border-radius-lg;
  box-shadow: $shadow-lg, 0 0 30px rgba($primary-color, 0.18);
  border: 1px solid rgba($primary-color, 0.25);
  backdrop-filter: blur(18px);
  overflow: hidden;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg,
      $primary-color 0%,
      $accent-color 50%,
      $primary-color 100%);
    opacity: 0.8;
  }
}

.form-header {
  text-align: center;
  padding: $spacing-xl $spacing-lg $spacing-lg;
  background: linear-gradient(135deg, $white 0%, $gray-50 100%);
  border-bottom: 1px solid $gray-200;

  .form-title {
    font-size: 1.8rem;
    font-weight: 700;
    color: $gray-900;
    margin: 0 0 $spacing-sm 0;
    background: linear-gradient(135deg, $primary-color 0%, $accent-color 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .form-subtitle {
    font-size: 1rem;
    color: $gray-600;
    margin: 0;
    line-height: 1.5;
  }
}

.upload-section {
  padding: $spacing-lg;
  border-bottom: 1px solid $gray-200;
}

.translation-settings {
  padding: $spacing-lg;
}

.language-selection {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: $spacing-lg;
  align-items: end;
  margin-bottom: $spacing-lg;

  .language-group {
    display: flex;
    flex-direction: column;
    gap: $spacing-xs;

    .language-label {
      display: flex;
      align-items: center;
      gap: $spacing-xs;
      font-size: 0.9rem;
      font-weight: 600;
      color: $gray-700;

      .iconify {
        color: $accent-color !important;
        filter: drop-shadow(0 1px 3px rgba($accent-color, 0.4));
      }
    }

    .language-select {
      width: 100%;
    }
  }

  .arrow-icon {
    color: $accent-color;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 40px;
    filter: drop-shadow(0 1px 3px rgba($accent-color, 0.3));
  }
}

.form-actions {
  display: flex;
  justify-content: center;
  gap: $spacing-md;
  padding: $spacing-lg;
  border-top: 1px solid $gray-200;
  background-color: $gray-50;

  button {
    display: flex;
    align-items: center;
    gap: $spacing-xs;
    padding: $spacing-sm $spacing-lg;
    border: none;
    border-radius: $border-radius-md;
    font-size: 0.9rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  .translate-button {
    background-color: $primary-color;
    color: $white;
    font-weight: 600;

    &:hover:not(:disabled) {
      background-color: $primary-dark;
      transform: translateY(-1px);
      box-shadow: $shadow-md;
    }
  }
}

// 响应式设计
@media (max-width: 768px) {
  .form-header {
    padding: $spacing-lg $spacing-md $spacing-md;

    .form-title {
      font-size: 1.5rem;
    }

    .form-subtitle {
      font-size: 0.9rem;
    }
  }

  .upload-section,
  .translation-settings {
    padding: $spacing-md;
  }

  .language-selection {
    grid-template-columns: 1fr;
    gap: $spacing-md;
    text-align: center;

    .arrow-icon {
      transform: rotate(90deg);
    }
  }

  .form-actions {
    flex-direction: column;
    gap: $spacing-sm;

    button {
      width: 100%;
      justify-content: center;
    }
  }
}

/* 当进度替换上传区域时的容器样式 */
.upload-section :deep(.progress-replacer),
.upload-section .progress-replacer {
  border: 2px dashed rgba($primary-color, 0.4);
  border-radius: $border-radius-lg;
  padding: $spacing-lg;
  background: $white;
}

/* 上传区下方的小提示 */
.upload-hint {
  margin: $spacing-sm 0 0 0;
  font-size: 12px;
  color: $gray-500;
  .hint-link {
    color: $primary-color;
    text-decoration: underline;
  }
}

.page-count-explanation {
  display: flex;
  justify-content: center;
  margin-top: $spacing-md;

  .rule-trigger {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 0.85rem;
    color: $gray-500;
    cursor: pointer;
    transition: color 0.2s;

    &:hover {
      color: $primary-color;
    }
  }
}

.rule-content {
  .rule-group {
    &:not(:last-child) {
      margin-bottom: 12px;
    }

    .rule-title {
      font-weight: 600;
      color: #333; // Fallback or use global vars if available in this context
      font-size: 0.9rem;
      margin-bottom: 6px;
      border-bottom: 1px solid #eee;
      padding-bottom: 4px;
    }

    .rule-list {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .rule-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 0.85rem;

      .item-label {
        color: #555;
      }

      .item-ext {
        color: #999;
        font-family: monospace;
        background: #f5f7fa;
        padding: 1px 4px;
        border-radius: 4px;
      }
    }
  }
}

// 平板端适配
@media (min-width: 769px) and (max-width: 1024px) {
  .language-selection {
    grid-template-columns: 1fr auto 1fr;
    gap: $spacing-md;
  }
}

// 动画效果
.translation-form {
  animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// 按钮点击效果
.form-actions button {
  &:active:not(:disabled) {
    transform: translateY(1px);
  }
}

.rotating {
  animation: rotate 1s linear infinite;
}
@keyframes rotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.scanned-warning {
  display: flex;
  align-items: flex-start;
  gap: $spacing-md;
  padding: $spacing-lg;
  background: linear-gradient(135deg, #fff7ed 0%, #fff1f2 100%);
  border: 1px solid #fecaca;
  border-radius: $border-radius-lg;

  .scanned-warning-icon {
    flex-shrink: 0;
    color: #f97316;
    margin-top: 2px;
  }

  .scanned-warning-content {
    flex: 1;

    .scanned-warning-title {
      margin: 0 0 $spacing-sm 0;
      font-size: 1.05rem;
      font-weight: 700;
      color: #9a3412;
    }

    .scanned-warning-desc {
      margin: 0 0 $spacing-md 0;
      font-size: 0.9rem;
      color: #b45309;
      line-height: 1.6;
    }

    .reselect-button {
      display: inline-flex;
      align-items: center;
      gap: $spacing-xs;
      padding: $spacing-sm $spacing-lg;
      background-color: #fff;
      border: 1px solid #f97316;
      border-radius: $border-radius-md;
      color: #f97316;
      font-size: 0.9rem;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s ease;

      &:hover {
        background-color: #f97316;
        color: #fff;
      }
    }
  }
}
</style>
